// Panel lateral derecho del asistente (380px, empuja el contenido; en pantallas
// chicas ocupa toda la pantalla con botón de volver). Lo monta LayoutWAM.
import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FaArrowLeft, FaCheck, FaExclamationTriangle, FaExpandAlt, FaFileExcel, FaFilePdf, FaPaperclip, FaPaperPlane, FaPlus, FaRobot, FaTimes } from 'react-icons/fa';
import { nombreConsorcio, useAsistente } from './AsistenteContext';
import { descargarArchivo, subirExtracto, textoExtractoAdjunto } from './asistenteApi';
import MensajeMarkdown, { tieneTabla } from './MensajeMarkdown';
import TerminosAsistente from './TerminosAsistente';
import AccionCard from './AccionCard';

export const ANCHO_PANEL = 380;

export const sugerenciasPara = (consorcio) =>
  consorcio
    ? [
        '¿Quiénes deben 3 meses o más?',
        '¿Cuánto suma la deuda vencida y cuántos morosos hay?',
        '¿Cómo están la caja y los bancos?',
        '¿Qué reclamos siguen abiertos?',
      ]
    : ['¿Qué consorcios administro?', '¿Qué reclamos siguen abiertos en todos mis consorcios?'];

const usd = (v) => `US$ ${Number(v || 0).toFixed(Number(v || 0) < 0.01 ? 4 : 2)}`;

const Herramientas = ({ herramientas }) => {
  if (!herramientas?.length) return null;
  return (
    <VStack align="stretch" spacing={1} mb={2}>
      {herramientas.map((h) => (
        <HStack key={h.id} spacing={2} fontSize="xs" color="gray.500">
          {h.estado === 'corriendo' && <Spinner size="xs" />}
          {h.estado === 'listo' && <FaCheck />}
          {h.estado === 'error' && <FaExclamationTriangle color="#DD6B20" />}
          <Text>
            {h.etiqueta}
            {h.estado === 'corriendo' ? '…' : ''}
            {h.estado === 'error' ? ' (no se pudo)' : ''}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
};

const ICONOS = { pdf: <FaFilePdf />, xlsx: <FaFileExcel /> };

// Botones de descarga de un archivo generado por una herramienta (revisión de liquidación).
const Descargas = ({ archivos }) => {
  const [bajando, setBajando] = useState(null);
  const [fallo, setFallo] = useState(false);
  if (!archivos?.length) return null;
  const bajar = async (archivo, f) => {
    setBajando(f.url);
    setFallo(false);
    try {
      await descargarArchivo(f.url, `revision_liquidacion_${archivo.revision_id}.${f.formato}`);
    } catch {
      setFallo(true);
    } finally {
      setBajando(null);
    }
  };
  return (
    <VStack align="stretch" spacing={1} mt={2}>
      {archivos.map((archivo) => (
        <Wrap key={archivo.revision_id} spacing={2}>
          {archivo.formatos.map((f) => (
            <WrapItem key={f.url}>
              <Button size="xs" variant="outline" leftIcon={ICONOS[f.formato]} isLoading={bajando === f.url}
                onClick={() => bajar(archivo, f)}>
                {`Descargar ${f.etiqueta}`}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
      ))}
      {fallo && (
        <Text fontSize="xs" color="orange.600">
          No se pudo bajar el archivo. Probá de nuevo.
        </Text>
      )}
    </VStack>
  );
};

const ErrorConDetalle = ({ mensaje, detalle }) => {
  const [ver, setVer] = useState(false);
  return (
    <Box fontSize="sm" color="orange.600" mt={1}>
      <Text>{mensaje}</Text>
      {detalle && (
        <>
          <Button size="xs" variant="link" mt={1} onClick={() => setVer((v) => !v)}>
            {ver ? 'Ocultar detalle técnico' : 'Ver detalle técnico'}
          </Button>
          <Collapse in={ver}>
            <Text fontSize="xs" color="gray.500" mt={1} wordBreak="break-word">
              {detalle}
            </Text>
          </Collapse>
        </>
      )}
    </Box>
  );
};

const Mensaje = ({ m, fase, nota, ultimo }) => {
  const { ampliar, actualizarAccion } = useAsistente();
  const fondoUsuario = useColorModeValue('blue.50', 'whiteAlpha.200');
  if (m.rol === 'user') {
    return (
      <Flex justify="flex-end">
        <Box bg={fondoUsuario} px={3} py={2} borderRadius="lg" maxW="85%" fontSize="sm" whiteSpace="pre-wrap">
          {m.texto}
          {m.error && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {m.error}
            </Text>
          )}
        </Box>
      </Flex>
    );
  }
  const mostrarAmpliar = !m.pendiente && m.texto && (tieneTabla(m.texto) || m.texto.length > 900);
  return (
    <Box>
      <Herramientas herramientas={m.herramientas} />
      {m.pendiente && ultimo && fase !== 'escribiendo' && (
        <VStack align="stretch" spacing={1} mb={2}>
          <HStack spacing={2} fontSize="sm" color="gray.500" data-testid="asistente-pensando">
            <Spinner size="xs" />
            <Text>{fase === 'herramienta' ? 'Consultando WAM…' : 'Pensando…'}</Text>
          </HStack>
          {nota && (
            <Text fontSize="xs" color="gray.500" fontStyle="italic" noOfLines={2}>
              {nota}
            </Text>
          )}
        </VStack>
      )}
      {m.texto && <MensajeMarkdown texto={m.texto} />}
      <Descargas archivos={m.archivos} />
      {(m.acciones || []).length > 0 && (
        <VStack align="stretch" spacing={2} mt={2}>
          {m.acciones.map((accion) => (
            <AccionCard key={accion.id} accion={accion} onCambio={actualizarAccion} />
          ))}
        </VStack>
      )}
      {m.error && <ErrorConDetalle mensaje={m.error} detalle={m.detalle} />}
      {!m.pendiente && (m.costoUsd || mostrarAmpliar) && (
        <HStack justify="space-between" mt={1}>
          {mostrarAmpliar ? (
            <Button size="xs" variant="ghost" leftIcon={<FaExpandAlt />} onClick={() => ampliar(m.texto)}>
              Ampliar
            </Button>
          ) : (
            <span />
          )}
          {m.costoUsd && (
            <Text fontSize="10px" color="gray.400">
              {usd(m.costoUsd)} · {Number(m.tokensEntrada || 0).toLocaleString('es-AR')} entrada ·{' '}
              {Number(m.tokensSalida || 0).toLocaleString('es-AR')} salida
            </Text>
          )}
        </HStack>
      )}
    </Box>
  );
};

// Mensajes, términos pendientes y compositor: lo comparten el panel lateral y el módulo Asistente.
export const ChatCuerpo = () => {
  const a = useAsistente();
  const [texto, setTexto] = useState('');
  const finRef = useRef(null);
  const entradaRef = useRef(null);
  const borde = useColorModeValue('gray.200', 'whiteAlpha.300');
  const ultimoTexto = a.mensajes?.length ? a.mensajes[a.mensajes.length - 1].texto : '';

  useEffect(() => {
    if (finRef.current?.scrollIntoView) finRef.current.scrollIntoView({ block: 'end' });
  }, [a.mensajes?.length, ultimoTexto, a.fase]);

  useEffect(() => {
    if (entradaRef.current) entradaRef.current.focus();
  }, [a.abierto]);

  // Extracto adjunto (conciliación asistida): se sube al elegirlo y viaja con el próximo mensaje.
  const archivoRef = useRef(null);
  const [adjunto, setAdjunto] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorAdjunto, setErrorAdjunto] = useState('');
  const puedeAdjuntar = Boolean(a.estado?.puede_adjuntar_extracto) && Boolean(a.consorcio);
  const elegirArchivo = async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo || !a.consorcio) return;
    setSubiendo(true);
    setErrorAdjunto('');
    try {
      setAdjunto(await subirExtracto(archivo, a.consorcio.id));
    } catch (err) {
      setErrorAdjunto(err?.response?.data?.detail || 'No se pudo subir el extracto. Probá de nuevo.');
    } finally {
      setSubiendo(false);
    }
  };

  const mandar = (valor) => {
    let t = String(valor ?? texto).trim();
    if (adjunto) t = `${textoExtractoAdjunto(adjunto)}\n${t || 'Conciliá este extracto paso por paso.'}`;
    if (!t || a.enCurso) return;
    a.enviar(t);
    setTexto('');
    setAdjunto(null);
  };
  const sugerencias = sugerenciasPara(a.consorcio);
  const limite = Number(a.estado?.limite_diario_usd || 0);
  const gastado = Number(a.estado?.gastado_hoy_usd || 0);

  return (
    <>
    <Box flex="1" overflowY="auto" px={3} py={3}>
      {a.terminos ? (
        <TerminosAsistente />
      ) : !a.mensajes.length ? (
        <VStack align="stretch" spacing={3}>
          <Text fontSize="sm" color="gray.600">
            Preguntame por morosos, estados de cuenta, caja y bancos o reclamos. Solo consulto: no modifico nada.
          </Text>
          {!a.consorcio && (
            <Text fontSize="xs" color="gray.500">
              Elegí un consorcio arriba para preguntar por ese consorcio.
            </Text>
          )}
          <Wrap spacing={2}>
            {sugerencias.map((s) => (
              <WrapItem key={s}>
                <Button size="xs" variant="outline" borderRadius="full" whiteSpace="normal" h="auto" py={1.5}
                  textAlign="left" onClick={() => mandar(s)}>
                  {s}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </VStack>
      ) : (
        <VStack align="stretch" spacing={4}>
          {a.mensajes.map((m, i) => (
            <Mensaje key={m.id} m={m} fase={a.fase} nota={a.nota} ultimo={i === a.mensajes.length - 1} />
          ))}
        </VStack>
      )}
      <div ref={finRef} />
    </Box>

    {!a.terminos && (
      <Box px={3} py={2} borderTopWidth="1px" borderColor={borde}>
        {(adjunto || subiendo || errorAdjunto) && (
          <HStack mb={2} spacing={2} data-testid="asistente-adjunto">
            {subiendo && <Spinner size="xs" />}
            {subiendo && <Text fontSize="xs">Leyendo el extracto…</Text>}
            {adjunto && (
              <Badge colorScheme="blue" textTransform="none" fontWeight="normal" px={2} py={1}>
                {adjunto.nombre} · {adjunto.renglones} renglones
              </Badge>
            )}
            {adjunto && (
              <IconButton aria-label="Quitar el extracto" icon={<FaTimes />} size="xs" variant="ghost"
                onClick={() => setAdjunto(null)} />
            )}
            {errorAdjunto && <Text fontSize="xs" color="red.500">{errorAdjunto}</Text>}
          </HStack>
        )}
        <HStack align="flex-end" spacing={2}>
          {puedeAdjuntar && (
            <>
              <input ref={archivoRef} type="file" hidden accept=".csv,.txt,.xls,.xlsx,.pdf"
                data-testid="asistente-archivo" onChange={elegirArchivo} />
              <Tooltip label="Adjuntar extracto bancario (CSV, Excel o PDF) para conciliar">
                <IconButton aria-label="Adjuntar extracto" icon={<FaPaperclip />} size="sm" variant="ghost"
                  isDisabled={subiendo || a.enCurso} onClick={() => archivoRef.current?.click()} />
              </Tooltip>
            </>
          )}
          <Textarea
            ref={entradaRef}
            aria-label="Escribí tu pregunta"
            placeholder="Escribí tu pregunta…"
            size="sm"
            rows={2}
            resize="none"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                mandar();
              }
            }}
          />
          <IconButton
            aria-label="Enviar"
            icon={<FaPaperPlane />}
            colorScheme="blue"
            size="sm"
            isLoading={a.enCurso}
            isDisabled={(!texto.trim() && !adjunto) || subiendo}
            onClick={() => mandar()}
          />
        </HStack>
        {limite > 0 && (
          <HStack justify="space-between" mt={1}>
            <Text fontSize="10px" color="gray.400">
              Enter envía · Shift+Enter nueva línea
            </Text>
            <Badge colorScheme={gastado >= limite * 0.8 ? 'orange' : 'gray'} fontSize="10px" fontWeight="normal">
              Hoy {usd(gastado)} de {usd(limite)}
            </Badge>
          </HStack>
        )}
      </Box>
    )}
    </>
  );
};

const AsistentePanel = ({ top = 64 }) => {
  const a = useAsistente();
  const bg = useColorModeValue('white', 'gray.900');
  const borde = useColorModeValue('gray.200', 'whiteAlpha.300');

  if (!a.habilitado || !a.abierto) return null;

  const puedeElegir = Boolean(a.cambiarConsorcio) && a.consorcios?.length > 1;

  return (
    <>
      <Flex
        as="aside"
        aria-label="Asistente"
        direction="column"
        position="fixed"
        right={0}
        top={{ base: 0, md: `${top}px` }}
        bottom={0}
        w={{ base: '100%', md: `${ANCHO_PANEL}px` }}
        bg={bg}
        borderLeftWidth={{ base: 0, md: '1px' }}
        borderColor={borde}
        zIndex={{ base: 1400, md: 20 }}
        boxShadow={{ base: 'none', md: 'sm' }}
      >
        <Flex align="center" gap={2} px={3} py={2} borderBottomWidth="1px" borderColor={borde}>
          <IconButton
            aria-label="Volver"
            icon={<FaArrowLeft />}
            size="sm"
            variant="ghost"
            display={{ base: 'inline-flex', md: 'none' }}
            onClick={a.cerrar}
          />
          <FaRobot />
          <Box flex="1" minW={0}>
            <Text fontWeight="semibold" fontSize="sm">
              Asistente
            </Text>
            {puedeElegir ? (
              <Select
                aria-label="Consorcio del asistente"
                data-testid="asistente-selector-consorcio"
                size="xs"
                variant="unstyled"
                color="gray.500"
                fontSize="xs"
                h="auto"
                value={a.consorcio ? String(a.consorcio.id) : ''}
                onChange={(e) => {
                  const elegido = a.consorcios.find((c) => String(c.id) === e.target.value);
                  if (elegido) a.cambiarConsorcio(elegido);
                }}
              >
                {!a.consorcio && <option value="">Elegí un consorcio</option>}
                {a.consorcios.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {nombreConsorcio(c)}
                  </option>
                ))}
              </Select>
            ) : (
              <Text fontSize="xs" color="gray.500" noOfLines={1} data-testid="asistente-contexto">
                {a.consorcio ? nombreConsorcio(a.consorcio) : 'Todos tus consorcios'}
              </Text>
            )}
          </Box>
          <Tooltip hasArrow label="Nueva conversación">
            <IconButton aria-label="Nueva conversación" icon={<FaPlus />} size="sm" variant="ghost" onClick={a.nuevaConversacion} />
          </Tooltip>
          <Tooltip hasArrow label="Cerrar (Ctrl+K)">
            <IconButton
              aria-label="Cerrar asistente"
              icon={<FaTimes />}
              size="sm"
              variant="ghost"
              display={{ base: 'none', md: 'inline-flex' }}
              onClick={a.cerrar}
            />
          </Tooltip>
        </Flex>

        <ChatCuerpo />
      </Flex>

      <Modal isOpen={Boolean(a.ampliado)} onClose={() => a.ampliar(null)} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        {/* En el celular usa toda la pantalla para que las tablas anchas entren. */}
        <ModalContent
          maxW={{ base: '100vw', md: '6xl' }}
          my={{ base: 0, md: '3.75rem' }}
          minH={{ base: '100dvh', md: 'auto' }}
          borderRadius={{ base: 0, md: 'md' }}
        >
          <ModalHeader fontSize="md">Respuesta del asistente</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>{a.ampliado && <MensajeMarkdown texto={a.ampliado} compacto={false} />}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AsistentePanel;
